/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Element } from 'react';

import React from 'react';
import { Form, Col, Row, UncontrolledAlert as Alert } from 'reactstrap';
import {
  BUILDING_TYPE_DIR_1,
  BUILDING_TYPE_DIR_2,
  DESIGN_CODE_YEAR,
  SEISMICITY_DESIGN,
  SEISMICITY_SHAKING,
  YEAR_OF_CONSTRUCTION,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import ContinueToNextSectionGroupButton from '#components/pages/Models/Model/sections/support/buttons/ContinueToNextSectionGroupButton/index.jsx';
import SubmitButton from '#components/pages/Models/Model/sections/support/buttons/ModelFormSectionSubmitButton/index.jsx';
import { Field, FormGroup } from '#components/pages/Models/Model/support/form/index.js';
import YearOfConstructionOrCodeYear from '#components/pages/Models/Model/sections/support/forms/YearOfConstructionOrCodeYear/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import OccupancyFieldset from './OccupancyFieldset/index.js';
import StoriesFieldSet from './StoriesFieldSet/index.js';

type Props = {|
  error: string,
  fieldsByName: Object,
  form: string,
  group: string,
  heading: string,
  modelId: string,
  modelType: number,
  onBack: Function,
  onNext: Function,
  handleSubmit: Function,
  submitting: boolean,
|};

const PrimaryBuildingInfoSection = (props: Props): Element<'div'> => {
  const {
    error,
    fieldsByName,
    form,
    group,
    heading,
    modelId,
    modelType,
    onBack,
    onNext,
    handleSubmit,
    submitting,
  } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        <FormGroup tag="fieldset">
          <legend>Building Information</legend>
          <Row>
            <Col lg="9">
              Structural System <Tip tipId="structuralSystem" />
            </Col>
          </Row>
          <Row>
            <Col lg="9" className="ml-3">
              <FormGroup form={form} fields={[BUILDING_TYPE_DIR_1]}>
                <Field field={fieldsByName[BUILDING_TYPE_DIR_1]} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg="9" className="ml-3">
              <FormGroup form={form} fields={[BUILDING_TYPE_DIR_2]}>
                <Field field={fieldsByName[BUILDING_TYPE_DIR_2]} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup row>
            <Col lg="5">
              <YearOfConstructionOrCodeYear
                yearOfConstructionName={YEAR_OF_CONSTRUCTION}
                designCodeYearName={DESIGN_CODE_YEAR}
                fieldsByName={fieldsByName}
                form={form}
              />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[SEISMICITY_DESIGN]} row>
            <Col lg="5">
              <Field field={fieldsByName[SEISMICITY_DESIGN]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[SEISMICITY_SHAKING]} row>
            <Col lg="4">
              <Field field={fieldsByName[SEISMICITY_SHAKING]} />
            </Col>
          </FormGroup>
        </FormGroup>
        <StoriesFieldSet form={form} />
        <OccupancyFieldset form={form} />
        {error && !submitting ? <Alert color="danger">{error}</Alert> : ''}
        <BackButton onClick={onBack} />
        <SubmitButton modelType={modelType} modelId={modelId} />
        <ContinueToNextSectionGroupButton group={group} modelType={modelType} onClick={onNext} />
      </Form>
    </div>
  );
};

export default PrimaryBuildingInfoSection;
